import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { Instagram, CheckCircle2, XCircle, Loader2, AlertCircle, Clock, ExternalLink, ArrowLeft, Repeat } from 'lucide-react';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../components/ui/utils';
import { Badge } from '../../../components/ui/badge';
import type { Account } from '../../../entities/account';
import type { PublicationCategory } from '../../../shared/types/common';
import type { CopyrightStatus } from '../../../shared/api/copyright.api';
import { getCategoryLabel, getCategoryColor } from '../../../shared/lib/publicationCategories';
import { publicationsApi } from '../../../shared/api/publications.api';
import { ROUTES } from '../../../shared/config/routes';
import { parseScheduled, publishActionLabel } from '../lib/publishPlan';

// 'scheduled' — рендер закончен, публикация ждёт своего времени в очереди.
export type PublishStatus = 'pending' | 'publishing' | 'success' | 'error' | 'scheduled';

export interface AccountPublishStatus {
  account: Account;
  status: PublishStatus;
  caption: string;
  error?: string;
  progress?: number;
  // Granular phone-publish stage (when publishing via the on-device worker).
  stage?: string;
}

// Human label for a phone-publish stage; null for unknown/none.
export function stageLabel(stage: string | undefined): string | null {
  switch (stage) {
    case 'queued': return 'В очереди на телефон…';
    case 'claimed': return 'Телефон взял задачу…';
    case 'downloading': return 'Скачиваю видео…';
    case 'pushing': return 'Загружаю на телефон…';
    case 'scanning': return 'Готовлю галерею…';
    case 'switching_account': return 'Переключаю аккаунт…';
    case 'uploading': return 'Загружаю в Instagram…';
    case 'sharing': return 'Публикую…';
    case 'verifying': return 'Проверяю…';
    default: return null;
  }
}

interface OverallStatus {
  phase: 'processing' | 'uploading' | 'remuxing' | 'publishing';
  current: number;
  total: number;
  progress?: number;
  percent: number;
  indeterminate?: boolean;
}

interface PublishConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: AccountPublishStatus[];
  onConfirm: () => void;
  isPublishing: boolean;
  category?: PublicationCategory;
  copyrightStatus?: CopyrightStatus;
  overallStatus?: OverallStatus | null;
  videoDurationSec?: number | null;
  // Called when the user *explicitly* confirms closing the modal mid-publish.
  // The owner is expected to POST /api/reels/:jobId/cancel and tear down local
  // polling/XHR. If undefined, the modal falls back to the legacy behaviour
  // (close confirmation only, no backend cancel).
  onUserCancel?: () => void | Promise<void>;
  // Terminal "done": summary + failed list + OK.
  done?: boolean;
  onAck?: () => void;
  // Terminal "error": reason + Retry + OK; stays open until acknowledged.
  errorState?: boolean;
  errorReason?: string;
  onRetry?: () => void;
  // Orange banner shown during upload (big file / stalled).
  uploadWarning?: string | null;
  // Blue banner shown while the backend auto-retries an Instagram-side transient
  // publish failure (e.g. "Ошибка на стороне Instagram. Пробую ещё одну попытку…").
  retryNotice?: string | null;
  /** Время отложенной публикации (datetime-local или ISO); пусто — сразу. */
  scheduledAt?: string | null;
  /** Включено «Дублировать в сторис» — строка «+ сторис» в окне. */
  duplicateToStory?: boolean;
  /** batchId пакета — по нему после публикации берём ссылки на посты. */
  batchId?: string | null;
  /** Форма открыта из «Монтажа»/«Роликов B2G» — кнопка возврата туда. */
  returnTo?: { label: string; to: string } | null;
  /** «Ещё одну в эти же аккаунты»: форма очищается, галочки остаются. */
  onPublishAgain?: () => void;
}

/** Ссылки на вышедшие посты пакета. Журнал пишется следом за публикацией — даём ему пару попыток. */
function usePermalinks(batchId: string | null | undefined, enabled: boolean) {
  const [links, setLinks] = useState<{ id: string; name: string; url: string }[]>([]);
  useEffect(() => {
    setLinks([]);
    if (!enabled || !batchId) return;
    let cancelled = false;
    const load = async (attempt: number) => {
      try {
        const { publications } = await publicationsApi.getByBatchOrId(batchId);
        const found = publications
          .filter((p) => p.permalink)
          .map((p) => ({ id: p.id, name: p.accountName, url: p.permalink }));
        if (cancelled) return;
        if (found.length) setLinks(found);
        else if (attempt < 3) setTimeout(() => void load(attempt + 1), 3000);
      } catch {
        if (!cancelled && attempt < 3) setTimeout(() => void load(attempt + 1), 3000);
      }
    };
    void load(1);
    return () => { cancelled = true; };
  }, [batchId, enabled]);
  return links;
}

// Rough pre-publish estimate: render scales with video duration (libx264 on prod
// runs ~1.5× realtime), IG container processing is a ~40-90s tax per account
// roughly proportional to source length. Buckets are chosen from real prod
// timings observed across 9-100 MB / 23-180s clips.
function formatPublishEstimate(durationSec: number | null | undefined): string | null {
  if (!durationSec || !isFinite(durationSec) || durationSec <= 0) return null;
  if (durationSec < 30) return '~1-2 минуты';
  if (durationSec < 60) return '~2-3 минуты';
  if (durationSec < 120) return '~3-5 минут';
  if (durationSec < 180) return '~5-7 минут';
  return '~7-10 минут';
}

export function PublishConfirmationModal({
  open,
  onOpenChange,
  accounts = [],
  onConfirm,
  isPublishing,
  category,
  copyrightStatus,
  overallStatus,
  videoDurationSec,
  onUserCancel,
  done,
  onAck,
  errorState,
  errorReason,
  onRetry,
  uploadWarning,
  retryNotice,
  scheduledAt,
  duplicateToStory,
  batchId,
  returnTo,
  onPublishAgain,
}: PublishConfirmationModalProps) {
  const navigate = useNavigate();
  const scheduled = parseScheduled(scheduledAt);
  // Отложенная публикация рендерится сразу, но в ленту уходит позже — оценка
  // «займёт ~N мин» тут только путает.
  const estimate = scheduled ? null : formatPublishEstimate(videoDurationSec);
  const [confirmingExit, setConfirmingExit] = useState(false);
  const successCount = accounts.filter(a => a.status === 'success').length;
  const totalCount = accounts.length;
  const failed = accounts.filter(a => a.status === 'error');
  const errorCount = failed.length;
  const terminal = done || errorState;
  const permalinks = usePermalinks(batchId, !!done && !scheduled && successCount > 0);
  const actionLabel = publishActionLabel({ count: totalCount, scheduledAt: scheduled });
  /** Окно закрывается, форма сбрасывается — и только потом уходим. */
  const leaveTo = (to: string) => {
    onAck?.();
    navigate(to);
  };

  function handleOpenChange(v: boolean) {
    if (terminal && !v) { onAck?.(); return; } // close only via OK in terminal states
    if (!terminal && isPublishing && !v) { setConfirmingExit(true); }
    else if (!terminal && !isPublishing) { onOpenChange(v); }
  }

  const getStatusIcon = (status: PublishStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'publishing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'scheduled':
        return <Clock className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: PublishStatus) => {
    switch (status) {
      case 'success':
        return 'Опубликовано';
      case 'publishing':
        return 'Публикуется...';
      case 'error':
        return 'Ошибка';
      case 'scheduled':
        return 'Запланировано';
      default:
        return 'В очереди';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="relative">
        <DialogHeader>
          <DialogTitle>
            {errorState
              ? 'Не удалось опубликовать'
              : done
                ? (errorCount === 0
                    ? (scheduled ? 'Запланировано ✅' : 'Готово ✅')
                    : `Готово: ${successCount} успешно, ${errorCount} с ошибкой`)
                : isPublishing
                  ? (scheduled ? `Готовим отложенную публикацию…` : `Публикация ${successCount} из ${totalCount}`)
                  : actionLabel}
          </DialogTitle>
          <DialogDescription>
            {terminal ? (
              errorState
                ? 'Подробности ошибки ниже.'
                : scheduled
                  ? 'Видео готово, в ленту оно уйдёт в назначенное время. Очередь — в «Публикации → Отложенные».'
                  : 'Публикация завершена.'
            ) : (
              <>
                {!isPublishing && 'Проверьте список аккаунтов и описания перед публикацией'}
                {isPublishing && (() => {
                  const phase = overallStatus?.phase;
                  const tail = phase === 'publishing' && successCount > 0 && totalCount > 1
                    ? ` — опубликовано ${successCount} из ${totalCount}`
                    : '';
                  switch (phase) {
                    case 'uploading':  return `Загружаем видео на сервер…${tail}`;
                    case 'remuxing':   return `Сервер обрабатывает и рендерит видео…${tail}`;
                    case 'publishing': return `Публикуем в Instagram${tail}`;
                    case 'processing': return `Обрабатываем видео в браузере…${tail}`;
                    default:           return 'Подождите, идёт публикация…';
                  }
                })()}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Примерное время — плоский синий чип (как warning/success-боксы), синий =
            наш «инфо/прогресс» акцент. Пульс-иконка, чтобы попадал на глаз. */}
        {!terminal && !isPublishing && estimate && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
            <Clock className="h-4 w-4 shrink-0 animate-pulse" />
            <span>Публикация займёт <strong className="font-semibold">{estimate}</strong></span>
          </div>
        )}

        {/* Общий прогресс-бар */}
        {isPublishing && overallStatus && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {overallStatus.phase === 'processing' && `Обработка видео — аккаунт ${overallStatus.current} из ${overallStatus.total}`}
                {overallStatus.phase === 'uploading' && `Загрузка видео — аккаунт ${overallStatus.current} из ${overallStatus.total}`}
                {overallStatus.phase === 'remuxing' && `Нормализация видео — аккаунт ${overallStatus.current} из ${overallStatus.total}`}
                {overallStatus.phase === 'publishing' && `Публикация в Instagram — аккаунт ${overallStatus.current} из ${overallStatus.total}`}
              </span>
              <span className="font-medium tabular-nums">{overallStatus.indeterminate ? '' : `${overallStatus.percent}%`}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              {overallStatus.indeterminate ? (
                <div
                  className="h-full rounded-full bg-blue-500"
                  style={{ width: '40%', animation: 'progress-indeterminate 1.5s ease-in-out infinite' }}
                />
              ) : (
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-500"
                  style={{
                    width: `${overallStatus.percent}%`,
                    // While we sit at 90% waiting for Instagram's container to
                    // finish processing (no per-account success yet), overlay
                    // moving diagonal stripes so the editor sees motion instead
                    // of a frozen bar.
                    ...(overallStatus.phase === 'publishing' && overallStatus.percent < 100 && successCount === 0
                      ? {
                          backgroundImage:
                            'linear-gradient(45deg, rgba(255,255,255,0.25) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.25) 75%, transparent 75%, transparent)',
                          backgroundSize: '1rem 1rem',
                          animation: 'progress-stripes 0.8s linear infinite',
                        }
                      : {}),
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Auto-retry notice: Instagram-side transient publish failure is being
            retried by the backend. Shown in-window (not a toast) for the whole
            publish phase, since the retry happens at the IG-container step. */}
        {isPublishing && retryNotice && (
          <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
            <span className="mt-0.5 shrink-0">🔄</span>
            <span>{retryNotice}</span>
          </div>
        )}

        {/* Upload hints/warnings only matter while the file is still uploading
            (<90%). Past 90% it's Instagram container processing — slowness there
            is normal, so the "upload stalled" warning must not appear. */}
        {isPublishing && (overallStatus?.percent ?? 0) < 90 && !uploadWarning && (
          <p className="text-xs text-muted-foreground">Не сворачивайте вкладку и не блокируйте экран до конца загрузки.</p>
        )}
        {isPublishing && (overallStatus?.percent ?? 0) < 90 && uploadWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-400">
            <span className="mt-0.5 shrink-0">⚠️</span>
            <span>{uploadWarning}</span>
          </div>
        )}

        {copyrightStatus === 'flagged' && !isPublishing && (
          <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-400">
            <span className="mt-0.5 shrink-0">⚠️</span>
            <span>
              У этого видео обнаружено авторское право. Instagram может его заглушить или ограничить охват после публикации.
            </span>
          </div>
        )}

        {/* Тип контента — read-only */}
        {category && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Тип контента:</span>
            <Badge variant={getCategoryColor(category)}>{getCategoryLabel(category)}</Badge>
          </div>
        )}
        {!terminal && (scheduled || duplicateToStory) && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {scheduled && (
              <span className="inline-flex items-center gap-1"><Clock className="size-4" /> В очередь на {scheduled.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}</span>
            )}
            {duplicateToStory && <Badge variant="outline">+ сторис</Badge>}
          </div>
        )}

        {/* Зелёный баннер успеха в финальном окне (done) — явный сигнал.
            Показываем всегда (и на 1, и на пачку) — единый вид публикации. */}
        {done && successCount > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-green-700 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400">
            <CheckCircle2 className="h-6 w-6 shrink-0" />
            <span className="font-semibold">{scheduled ? 'Запланировано' : 'Опубликовано'} {successCount} из {totalCount}</span>
          </div>
        )}

        {done && permalinks.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-medium">Открыть в Instagram</p>
            <div className="flex flex-wrap gap-2">
              {permalinks.map((l) => (
                <Button key={l.id} asChild variant="outline" size="sm">
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-4" />
                    {permalinks.length === 1 ? 'Открыть в Instagram' : l.name}
                  </a>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Плитки фиксированного размера (w-24 = 96px), flex с переносом и
            центрированием: 1 аккаунт — небольшая плитка по центру, много —
            переносятся рядами по центру. Никакого раздувания на 1-3 аккаунта. */}
        <div className="flex flex-wrap justify-center gap-3 py-4">
          {accounts.map(({ account, status, caption, error, progress, stage }) => (
            <div
              key={account.id}
              style={status === 'success' ? { animation: 'success-glow 1.6s ease-in-out infinite' } : undefined}
              className={cn(
                'relative w-24 aspect-square rounded-lg border-2 transition-colors overflow-hidden',
                status === 'success' && 'border-green-500 bg-green-50/50 dark:bg-green-950/20',
                status === 'publishing' && 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20',
                status === 'error' && 'border-red-500 bg-red-50/50 dark:bg-red-950/20',
                status === 'pending' && 'border-border'
              )}
            >
              {/* Аватар на весь размер */}
              <Avatar className="h-full w-full rounded-none">
                {/* key по src — свежий <img> на каждый аккаунт, без "прилипшего" кадра чужого аккаунта */}
                <AvatarImage key={account.profilePicture || account.id} src={account.profilePicture} alt={account.name} className="object-cover" />
                <AvatarFallback>
                  <Instagram className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>

              {/* Прогресс-бар снизу при обработке */}
              {status === 'publishing' && (
                <div className="absolute bottom-0 left-0 right-0">
                  {progress !== undefined ? (
                    <>
                      <div className="h-1 bg-black/30">
                        <div
                          className="h-full bg-blue-400 transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="bg-black/60 text-white text-[10px] text-center py-0.5 font-medium">
                        {Math.round(progress)}%
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-1 bg-black/30 overflow-hidden">
                        <div className="h-full bg-blue-400" style={{ width: '40%', animation: 'progress-indeterminate 1.5s ease-in-out infinite' }} />
                      </div>
                      <div className="bg-black/60 text-white text-[9px] text-center py-0.5 font-medium px-0.5 leading-tight">
                        {stageLabel(stage) ?? 'Загрузка...'}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Иконка статуса в углу */}
              <div className="absolute top-1 right-1 bg-background/80 rounded-full p-1">
                {getStatusIcon(status)}
              </div>

              {/* Ошибка при наведении */}
              {error && (
                <div className="absolute inset-0 bg-red-500/90 flex items-center justify-center p-2 opacity-0 hover:opacity-100 transition-opacity">
                  <p className="text-xs text-white text-center">{error}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {errorState && errorReason && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/20 dark:text-red-400">
            <XCircle className="h-5 w-5 mt-0.5 shrink-0" />
            <span className="font-medium">{errorReason}</span>
          </div>
        )}

        {terminal && failed.length > 0 && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {failed.map(({ account, error }) => (
              <div key={account.id} className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm dark:border-red-800 dark:bg-red-950/20">
                <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{account.name}</p>
                  <p className="text-red-700 dark:text-red-400">{error || 'Ошибка публикации'}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {errorState ? (
            <>
              {onRetry && <Button variant="outline" onClick={onRetry}>Повторить</Button>}
              <Button onClick={onAck}>OK</Button>
            </>
          ) : done ? (
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              {returnTo && (
                <Button variant="outline" onClick={() => leaveTo(returnTo.to)}>
                  <ArrowLeft className="size-4" />
                  {returnTo.label}
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link to={scheduled ? `${ROUTES.PUBLICATIONS}/scheduled` : ROUTES.PUBLICATIONS} onClick={() => onAck?.()}>
                  {scheduled ? 'Перейти в Отложенные' : 'Перейти в Публикации'}
                </Link>
              </Button>
              <Button onClick={onPublishAgain ?? onAck}>
                <Repeat className="size-4" />
                Ещё одну в эти же аккаунты
              </Button>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={() => isPublishing ? setConfirmingExit(true) : onOpenChange(false)}>
                {isPublishing ? 'Закрыть' : 'Отмена'}
              </Button>
              {!isPublishing && <Button onClick={onConfirm} disabled={totalCount === 0}>{actionLabel}</Button>}
            </>
          )}
        </DialogFooter>

        {confirmingExit && (
          <div className="absolute inset-0 bg-background/95 flex flex-col items-center justify-center gap-4 rounded-lg z-10 p-6">
            <p className="text-sm font-medium text-center">Публикация ещё идёт. Вы точно хотите отменить?</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmingExit(false)}>Продолжить</Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  setConfirmingExit(false);
                  // Tell the backend to abort ffmpeg + skip publishing. If the
                  // owner didn't wire onUserCancel, fall back to just closing.
                  if (onUserCancel) {
                    await onUserCancel();
                  } else {
                    onOpenChange(false);
                  }
                }}
              >
                Отменить публикацию
              </Button>
            </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}